<template>
  <div class="relative nav-user-root">
    <div
      class="flex items-center gap-2 cursor-pointer px-2 py-1 rounded-md hover:bg-gray-100"
      @click="toggleDropdown"
    >
      <div>
        <img :src="avatarImg" alt="用户头像" class="w-8 h-8" />
      </div>
      <div class="flex flex-col max-md:hidden">
        <div class="text-sm font-medium leading-tight truncate">
          {{ user.nickname || user.userLogin || "用户" }}
        </div>
        <div class="text-xs text-gray-500 truncate">{{ channelName }}</div>
      </div>
      <div class="text-gray-500 max-md:hidden">
        <img :src="navImg" alt="下拉菜单" class="w-4 h-4" />
      </div>
    </div>

    <div
      v-if="showDropdown"
      class="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-2xl py-1 z-50"
    >
      <!-- 移动端：文档版本 + 返回主页面 -->
      <div class="md:hidden">
        <div class="px-4 py-2 text-xs text-gray-500">文档版本</div>
        <button
          v-for="version in versionList"
          :key="version.value"
          type="button"
          class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
          :class="{
            'bg-gray-100': version.value === currentVersion?.value,
          }"
          @click="handleSwitchVersion(version)"
        >
          {{ version?.label || "默认版本" }}
        </button>
        <div class="border-t border-gray-100 my-1"></div>
        <a
          :href="getBaseDomain()"
          target="_blank"
          class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          @click="showDropdown = false"
        >
          返回主页面
        </a>
        <div class="border-t border-gray-100 my-1"></div>
      </div>

      <a
        :href="getBaseDomain() + '/#/app-params'"
        target="_blank"
        class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
        @click="showDropdown = false"
      >
        <KeyOutlined class="w-4 h-4" />
        API Keys
      </a>

      <div class="border-t border-gray-100 my-1"></div>
      <button
        type="button"
        @click="handleLogout"
        class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
      >
        <img :src="logoutImg" alt="退出" class="w-4 h-4" />
        退出
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from "vue";
import { useUserStore } from "../stores/user";
import type { User } from "../stores/user";
import { KeyOutlined } from "@ant-design/icons-vue";
import { getBaseDomain } from "@/lib/config";
import { USER_CHANNEL, USER_CHANNEL_NAME } from "@/lib/constant";
import avatarImg from "@/assets/avatar-img.svg";
import navImg from "@/assets/nav.svg";
import logoutImg from "@/assets/logout.svg";

interface VersionItem {
  label?: string;
  value?: string;
  url?: string;
}

const props = withDefaults(
  defineProps<{
    versionList?: VersionItem[];
    currentVersion?: VersionItem | null;
  }>(),
  {
    versionList: () => [],
    currentVersion: null,
  },
);

const emit = defineEmits<{
  switchVersion: [version: VersionItem];
}>();

const userStore = useUserStore();
const user = computed<User | Record<string, never>>(
  () => userStore.userInfo || {},
);
const channelName = computed(() => {
  if (!user.value) {
    return "";
  }
  if (USER_CHANNEL.EMAIL === user.value.channel) {
    return user.value.email;
  }
  return USER_CHANNEL_NAME.get(user.value.channel!) || "";
});

const showDropdown = ref(false);

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const handleClickOutside = (e: Event) => {
  const target = e.target as HTMLElement;
  if (!target.closest(".nav-user-root")) {
    showDropdown.value = false;
  }
};

window.addEventListener("click", handleClickOutside);
onBeforeUnmount(() => {
  window.removeEventListener("click", handleClickOutside);
});

const handleSwitchVersion = (version: VersionItem) => {
  showDropdown.value = false;
  emit("switchVersion", version);
};

const handleLogout = async () => {
  showDropdown.value = false;
  await userStore.logout();
};
</script>

<style scoped>
.nav-user-root {
  position: relative;
}

.text-gray-500 {
  display: flex;
  align-items: center;
}
</style>
